"use client";

import {
  Button,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  IconButton,
  Spinner,
} from "@dafiti/closet-ui";
import { useResponsive } from "@dafiti/closet-ui/hooks";
import { Filter } from "@dafiti/closet-ui/icons";
import { useTranslations } from "next-intl";
import { useCallback } from "react";

import { useDrillNavigation } from "../../../hooks/useDrillNavigation.hook";
import { useKeyboardInset } from "../../../hooks/useKeyboardInset.hook";
import { useLiveUpdate } from "../../../hooks/useLiveUpdate.hook";
import { useMoreFilters } from "../../../hooks/useMoreFilters.hook";
import { MoreFiltersProvider } from "../../../providers";
import { DrawerFiltersHeader } from "../DrawerFiltersHeader";
import { FilterDrillScreen } from "../FilterDrillScreen";
import { FilterRootScreen } from "../FilterRootScreen";

interface MoreFiltersDrawerProps {
  hasActiveFilters: boolean;
  variant: "mobile" | "desktop";
}

export function MoreFiltersDrawer({
  hasActiveFilters,
  variant,
}: MoreFiltersDrawerProps) {
  const t = useTranslations("catalog");
  const { isMobile } = useResponsive();
  const direction = isMobile === false ? "right" : "left";

  const { isOpen, openDrawer, closeDrawer } = useMoreFilters();

  // Keep the footer above the keyboard on mobile: the side drawer is `h-full`
  // (layout viewport), so shrink it to the visible area when the keyboard opens.
  const { inset } = useKeyboardInset(isMobile === true && isOpen);
  const contentStyle = inset
    ? { height: `calc(100% - ${inset}px)` }
    : undefined;

  const {
    activeFacet,
    isDrillOpen,
    drillLabel,
    setDrillLabel,
    drillBackHandler,
    setDrillBackHandler,
    navigateToDrill,
    navigateToRoot,
    confirmDrill,
  } = useDrillNavigation();
  const { triggerLiveUpdate, isLiveUpdateLoading } = useLiveUpdate();

  const screen = isDrillOpen ? "drill" : "root";

  const handleDrillConfirm = async () => {
    await triggerLiveUpdate();
    confirmDrill();
  };

  const handleClose = () => {
    closeDrawer();
    navigateToRoot();
  };

  // Voltar condicional: sobe um nível na árvore de categorias enquanto houver
  // ancestral; no topo (ou em filtros planos) sai do drill.
  const handleBack = () => {
    if (drillBackHandler) drillBackHandler();
    else navigateToRoot();
  };

  // Envolve o setter em updater porque o handler é uma função (setState trataria
  // um argumento-função como updater de estado). useCallback mantém a identidade
  // estável para não re-disparar o efeito de registro no filho a cada render.
  const registerBackHandler = useCallback(
    (handler: (() => void) | null) => setDrillBackHandler(() => handler),
    [setDrillBackHandler],
  );

  const buttonVariant = hasActiveFilters ? "primary" : "secondary";

  return (
    <MoreFiltersProvider>
      <Drawer
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) handleClose();
        }}
        autoFocus
        direction={direction}
        repositionInputs={false}
      >
        {/* Single trigger per instance: visibility is controlled by the parent
        placement wrapper, so each test-id resolves to exactly one DOM node. */}
        <DrawerTrigger asChild onClick={openDrawer}>
          {variant === "mobile" ? (
            <IconButton
              variant={buttonVariant}
              size="md"
              aria-label={t("actions.moreFilters")}
              data-testid="more-filters-button-mobile"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <Filter className="size-3.5!" />
              </div>
            </IconButton>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              className="flex items-center gap-2 px-7"
              data-testid="more-filters-button-desktop"
            >
              <Filter className="size-4" />
              <span className="text-xs">{t("actions.moreFiltersShort")}</span>
            </Button>
          )}
        </DrawerTrigger>

        <DrawerContent
          aria-describedby={undefined}
          data-testid="more-filters-drawer"
          className="py-6 px-4 pb-0 flex flex-col h-full"
          style={contentStyle}
        >
          <div className="relative flex flex-col flex-1 min-h-0">
            {isLiveUpdateLoading && !isDrillOpen && (
              <div
                className="absolute inset-0 z-10 flex items-center justify-center bg-white/60"
                data-testid="more-filters-loading-overlay"
                aria-hidden="true"
              >
                <Spinner size="lg" ariaLabel={t("common.loadingLabel")} />
              </div>
            )}

            <DrawerFiltersHeader
              screen={screen}
              facetLabel={drillLabel ?? activeFacet?.label}
              onBack={handleBack}
              onClose={handleClose}
            />

            <div className="flex flex-col flex-1 min-h-0 px-2">
              {isDrillOpen && activeFacet ? (
                <FilterDrillScreen
                  facet={activeFacet}
                  onConfirm={handleDrillConfirm}
                  onLabelChange={setDrillLabel}
                  onBackHandlerChange={registerBackHandler}
                />
              ) : (
                <FilterRootScreen
                  onNavigate={navigateToDrill}
                  onApply={handleClose}
                />
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </MoreFiltersProvider>
  );
}
